import React, { useCallback, useEffect, useState } from "react";
import {
  Facebook, Sparkles, CheckCircle2, AlertTriangle, Circle, Loader2, RotateCw,
  PartyPopper, Pencil, Plus, ArrowRight,
} from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Button, Field, Input, Skeleton, Toast, Chip } from "@/ui";

function StepIcon({ status }) {
  if (status === "done") return <CheckCircle2 size={20} style={{ color: "var(--good)" }} />;
  if (status === "running") return <Loader2 size={20} className="spin" style={{ color: "var(--accent)" }} />;
  if (status === "failed") return <AlertTriangle size={20} style={{ color: "var(--bad)" }} />;
  return <Circle size={20} style={{ color: "var(--line)" }} />;
}

function guessSentence(g) {
  if (!g) return "";
  const loc = g.location ? ` in ${g.location}` : "";
  const busy = g.busiest ? `, and you're busiest ${g.busiest}` : "";
  return `I had a look at your Page. You're ${g.name}${loc}, you sell ${g.sells}${busy}. Right so far?`;
}

export default function SetupWizard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState("");

  // local editing state
  const [fb, setFb] = useState("");
  const [fixing, setFixing] = useState(false);
  const [edit, setEdit] = useState({});
  const [picked, setPicked] = useState([]);
  const [customItem, setCustomItem] = useState("");
  const [prices, setPrices] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/setup");
      setState(r.data);
      if (r.data.facebookUrl) setFb(r.data.facebookUrl);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ob = state?.onboarding || {};
  const stage = ob.stage || "connect_page";
  const started = state && state.status && state.status !== "not_started";
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const call = async (path, body, after) => {
    setBusy(true); setError("");
    try { const r = await api.post(path, body); setState(r.data); after?.(r.data); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };

  const readPage = () => call("/setup/read-page", { facebookUrl: fb || null }, (d) => {
    flash(d.onboarding?.aiUsed ? "Read your Page with AI." : "Read your Page.");
  });
  const yesThatsUs = () => call("/setup/confirm-guess", {}, (d) => {
    setPicked(d.onboarding?.guess?.topics || []);
  });
  const saveFix = () => call("/setup/confirm-guess", { guess: edit }, (d) => {
    setFixing(false); setPicked(d.onboarding?.guess?.topics || []);
  });
  const continueItems = () => call("/setup/items", { selected: picked });
  const finishPrices = () => {
    const items = (ob.items || []).map((it) => ({
      name: it.name, priceCentavos: Math.round(parseFloat(prices[it.name] || 0) * 100),
    }));
    call("/setup/prices", { items }, () => flash("Your shop is set up!"));
  };
  const runRemaining = () => call("/setup/run", {});
  const startOver = () => call("/setup/restart", {}, () => { setFixing(false); setPicked([]); setPrices({}); flash("Setup reset — let's go again."); });
  const retry = async (key) => {
    setRetrying(key); setError("");
    try { const r = await api.post(`/setup/steps/${key}/run`, {}); setState(r.data); }
    catch (e) { setError(friendlyError(e)); }
    finally { setRetrying(""); }
  };

  const togglePick = (t) => setPicked((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);
  const addCustom = () => {
    const v = customItem.trim();
    if (v && !picked.includes(v)) setPicked((p) => [...p, v]);
    setCustomItem("");
  };

  const provisioning = stage === "done" || started;
  const hasFailed = started && state.steps.some((s) => s.status === "failed");
  const g = ob.guess;

  return (
    <Layout title="Set up your shop" subtitle="We read your Page and make a start for you — no blank forms.">
      {loading ? (
        <Card><Skeleton style={{ height: 160 }} /></Card>
      ) : (
        <div className="stack" style={{ gap: 16, maxWidth: 680 }}>

          {/* 1. Connect the Page */}
          {stage === "connect_page" && !provisioning && (
            <Card data-testid="setup-connect-card" className="stack" style={{ gap: 14 }}>
              <div className="row" style={{ gap: 10, alignItems: "center" }}>
                <span className="ph-icon" style={{ margin: 0 }}><Facebook size={20} /></span>
                <div>
                  <div className="t-h2">Start with your Facebook Page</div>
                  <div className="muted t-small">We read it and show you a first draft to fix — you never start from nothing.</div>
                </div>
              </div>
              <Field label="Your Facebook Page link" htmlFor="fb"
                hint="Paste your Page address. We'll read your recent posts.">
                <Input id="fb" data-testid="setup-fb-input" value={fb}
                  onChange={(e) => setFb(e.target.value)} placeholder="https://facebook.com/yourshop" />
              </Field>
              {error ? <div className="err-text" data-testid="setup-error">{error}</div> : null}
              <Button variant="primary" data-testid="setup-read-page-btn" loading={busy} onClick={readPage}>
                <Sparkles size={16} /> Read my Page
              </Button>
            </Card>
          )}

          {/* 2. Confirm the guess */}
          {stage === "confirm_guess" && g && !provisioning && (
            <Card data-testid="setup-guess-card" className="stack" style={{ gap: 14 }}>
              <div className="row" style={{ gap: 8 }}>
                <Sparkles size={18} style={{ color: "var(--accent)" }} />
                <span className="t-label">{ob.aiUsed ? "Here's what I understood" : "Here's my best guess"}</span>
              </div>
              {!fixing ? (
                <>
                  <p className="t-body" data-testid="setup-guess-sentence" style={{ fontSize: 18, lineHeight: 1.5 }}>
                    {guessSentence(g)}
                  </p>
                  {error ? <div className="err-text">{error}</div> : null}
                  <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                    <Button variant="primary" data-testid="setup-guess-yes" loading={busy} onClick={yesThatsUs}>
                      Yes, that's us
                    </Button>
                    <Button data-testid="setup-guess-fix" onClick={() => { setEdit({ name: g.name, location: g.location, sells: g.sells, busiest: g.busiest }); setFixing(true); }}>
                      <Pencil size={15} /> Almost — let me fix it
                    </Button>
                  </div>
                </>
              ) : (
                <div className="stack" style={{ gap: 10 }}>
                  <Field label="Your shop name"><Input data-testid="fix-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
                  <Field label="Where you are"><Input data-testid="fix-location" value={edit.location || ""} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="e.g. Cainta, Rizal" /></Field>
                  <Field label="What you sell"><Input data-testid="fix-sells" value={edit.sells || ""} onChange={(e) => setEdit({ ...edit, sells: e.target.value })} placeholder="e.g. home-cooked meals" /></Field>
                  <Field label="When you're busiest (optional)"><Input data-testid="fix-busiest" value={edit.busiest || ""} onChange={(e) => setEdit({ ...edit, busiest: e.target.value })} placeholder="e.g. around lunch" /></Field>
                  <div className="row" style={{ gap: 10 }}>
                    <Button variant="primary" data-testid="fix-save" loading={busy} onClick={saveFix}>Save</Button>
                    <Button variant="ghost" onClick={() => setFixing(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* 3. Tap what they sell */}
          {stage === "pick_items" && !provisioning && (
            <Card data-testid="setup-items-card" className="stack" style={{ gap: 14 }}>
              <div>
                <div className="t-h2">Which of these do you actually sell?</div>
                <div className="muted t-small">These are the things you post about most. Tap the ones you sell.</div>
              </div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }} data-testid="setup-topic-chips">
                {(g?.topics || []).map((t) => (
                  <button key={t} type="button" data-testid={`topic-${t}`}
                    className={`pill${picked.includes(t) ? " on" : ""}`} onClick={() => togglePick(t)}>
                    {picked.includes(t) ? <CheckCircle2 size={14} /> : null} {t}
                  </button>
                ))}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <Input data-testid="setup-custom-item" value={customItem} placeholder="Add another…"
                  onChange={(e) => setCustomItem(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCustom()} />
                <Button data-testid="setup-add-custom" onClick={addCustom}><Plus size={15} /> add</Button>
              </div>
              {error ? <div className="err-text">{error}</div> : null}
              <Button variant="primary" data-testid="setup-items-continue" loading={busy}
                disabled={!picked.length} onClick={continueItems}>
                Continue <ArrowRight size={16} />
              </Button>
            </Card>
          )}

          {/* 4. Prices */}
          {stage === "set_prices" && !provisioning && (
            <Card data-testid="setup-prices-card" className="stack" style={{ gap: 14 }}>
              <div>
                <div className="t-h2">Last thing — how much for each?</div>
                <div className="muted t-small">Just the price. We'll create these as real items in your shop.</div>
              </div>
              <div className="stack" style={{ gap: 10 }}>
                {(ob.items || []).map((it) => (
                  <div key={it.name} className="row-between" style={{ gap: 12 }} data-testid={`price-row-${it.name}`}>
                    <span style={{ flex: 1 }}>{it.name}</span>
                    <div className="row" style={{ gap: 6, alignItems: "center" }}>
                      <span className="muted">{"\u20b1"}</span>
                      <Input style={{ width: 110 }} inputMode="decimal" data-testid={`price-${it.name}`}
                        value={prices[it.name] || ""} placeholder="0"
                        onChange={(e) => setPrices({ ...prices, [it.name]: e.target.value })} />
                    </div>
                  </div>
                ))}
              </div>
              {error ? <div className="err-text">{error}</div> : null}
              <Button variant="primary" data-testid="setup-finish-btn" loading={busy} onClick={finishPrices}>
                Finish setup
              </Button>
            </Card>
          )}

          {/* 5. Behind-the-scenes provisioning + ready */}
          {provisioning && (
            <>
              {state.ready ? (
                <Card data-testid="setup-ready-banner" className="row-between" style={{ gap: 12, borderColor: "var(--good)" }}>
                  <div className="row" style={{ gap: 12 }}>
                    <PartyPopper size={22} style={{ color: "var(--good)" }} />
                    <div>
                      <div className="t-h3">Your shop is ready.</div>
                      <div className="muted t-small">Your items are in and your assistant can chat with customers.</div>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" data-testid="setup-start-over-btn" loading={busy} onClick={startOver}>
                    <RotateCw size={14} /> Start over
                  </Button>
                </Card>
              ) : (
                <Card className="row-between" data-testid="setup-progress-card">
                  <div>
                    <div className="t-h3">Setting up your shop</div>
                    <div className="muted t-small">{hasFailed ? "One step needs another try." : "Almost there — this only takes a moment."}</div>
                  </div>
                  <Button data-testid="setup-run-remaining-btn" onClick={runRemaining} loading={busy}>Keep going</Button>
                </Card>
              )}
              <Card data-testid="setup-steps-card" className="stack" style={{ gap: 0 }}>
                {state.steps.map((s, i) => (
                  <div key={s.key} data-testid={`setup-step-${s.key}`} className="row" style={{ alignItems: "flex-start", gap: 12, padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
                    <div style={{ marginTop: 2 }}><StepIcon status={s.status} /></div>
                    <div style={{ flex: 1 }}>
                      <div className="t-body" data-testid={`setup-step-label-${s.key}`}>{s.label}</div>
                      {s.message && s.status === "failed" ? <div className="err-text t-small" data-testid={`setup-step-msg-${s.key}`}>{s.message}</div>
                        : s.message && s.status === "done" ? <div className="muted t-small">{s.message}</div> : null}
                    </div>
                    {s.status === "failed" ? (
                      <Button size="sm" data-testid={`setup-retry-${s.key}`} loading={retrying === s.key} onClick={() => retry(s.key)}>
                        <RotateCw size={14} /> Try again
                      </Button>
                    ) : null}
                  </div>
                ))}
              </Card>
            </>
          )}
        </div>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="setup-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
